import { DocumentType } from "../pages/employees/enums/document-type.enum";
import { ClientTypeApi } from "./client.model";

export enum VehicleType {
    MICRO = 0,
    SEDAN = 1,
    CUV = 2,
    SUV = 3,
    HATCHBACK = 4,
    ROADSTER = 5,
    PICKUP = 6,
    VAN = 7,
    COUPE = 8,
    SUPERCAR = 9,
    CAMPERVAN = 10,
    MINITRUCK = 11,
    CABRIOLET = 12,
    MINIVAN = 13,
    TRUCK = 14
}

export enum FuelType {
    UNKNOWN = 0,
    GASOLINE_95 = 1,
    GASOLINE_98 = 2,
    DIESEL = 3,
    DIESEL_PREMIUM = 4,
    HYBRID = 5,
    ELECTRIC = 6,
    NATURAL_GAS = 7,
    _95E10 = 8,
    FUEL_92 = 9,
    OTHER = 20,
}

export enum AgendaStatus {
    PENDING = 0,
    CONFIRMED = 1,
    CANCELED = 2,
    COMPLETED = 3
}

export enum AgendaSource {
    Manual = 1,
    Budget = 2,
    Order = 3,
    Widget = 4
}

export interface Agenda {
    id: number;
    agendaDateTimeUtc: string;
    serviceDuration: string;
    duration?: string;
    durationInDays?: number;
    status?: AgendaStatus;
    source?: AgendaSource;
    sourceId?: string;
    logisticsStatus?: number;
    clientFullName: string;
    vehicleInfo: string;
    vehicleVin?: string;
    vehicleLicensePlate?: string;
    serviceName: string;
    employeeName: string;
    employeeId?: number;
    jobPositionId?: number;
    jobPositionName?: string;
    centerId: number;
    centerName?: string;
    employee?: { id: number; fullName: string; };
}

export interface AgendaDetail {
    id: number;
    centerId: number;
    agendaDateTimeUtc: string;
    duration?: string;
    status: AgendaStatus;
    source: AgendaSource;
    sourceId?: string;
    client: Client;
    vehicle: Vehicle;
    service: Service;
    employee: Employee;
    serviceOrder?: AssociatedServiceOrder;
}

export interface AgendaPreview {
    id: number;
    employeeId?: number;
    employee?: { id?: number; fullName?: string } | null;
    agendaDateTimeUtc?: string;
    status?: AgendaStatus;
    centerId?: number;
    service?: {
        name?: string;
        duration?: string;
        description?: string;
        category?: string;
        price?: number;
        serviceCode?: string;
    };
    serviceName?: string;
    serviceDuration?: string;
    clientFullName?: string;
    vehicleInfo?: string;
    vehicleVin?: string;
    vehicleLicensePlate?: string;
    jobPositionId?: number;
    jobPositionName?: string | null;
    isFromLanding?: boolean;
    isFromBudget?: boolean;
    isMultiDay?: boolean;
    duration?: string;
    durationInDays?: number;
    source?: AgendaSource;
    sourceId?: string;
}

export interface Client {
    id: number;
    fullName: string;
    email: string;
    phoneNumber: string;
    lastVisit: string;
    documentType: DocumentType;
    documentId: string;
    address: string;

}

export interface Vehicle {
    id: number;
    brand: string;
    vehicleType: VehicleType;
    fuelType: FuelType;
    model: string;
    vin: string;
    licensePlate: string;
    firstRegistrationDate: string;
    clientId: number;
    clientFullName: string;
}

export interface Service {
    id: number;
    name: string;
    description: string;
    category: string;
    serviceCode: string;
    duration: string;
    price: number;
    typePrice: number;
    imageUrl: string;
}

export interface Employee {
    id: number;
    fullName: string;
    position: string;
    phoneNumber: string;
    email: string;
    profilePictureUrl: string;
    joinDate: string;
    status: string;
}

export interface CreateAgendaDto {
    serviceTypeId: number;
    agendaDateTimeUtc: string;
    employeeId: number;
    centerId: number;
    existingClientId?: number;
    newClient?: {
        clientType?: ClientTypeApi;
        fullName: string;
        email: string;
        phone: string;
        documentType: DocumentType;
        documentId: string;
        address: string;
    };
    existingVehicleId?: number;
    newVehicle?: {
        brand: string;
        vehicleModel: string;
        vehicleType: VehicleType;
        fuelType: FuelType;
        vin?: string;
        licensePlate: string;
        firstRegistrationDate: string | null;
        manufacturingDate: string | null;
    };
    jobPositionId?: number;
    force?: boolean;
    source?: AgendaSource;
    sourceId?: string;
    stockReserved?: {
        productId: number;
        storeId?: number;
        quantity: number;
        isReserved: boolean;
    }[];
}

export interface UpdateAgendaDto {
    id?: number;
    employeeId: number | null;
    agendaDateTimeUtc: string;
    duration?: string;
    status?: AgendaStatus;
    jobPositionId?: number;
    force?: boolean;
    notify?: boolean;
    stockReserved?: {
        productId: number;
        storeId?: number;
        quantity: number;
        isReserved: boolean;
    }[];
}

export interface ConfirmAgendaDto {
    agendaId: number;
    employeeId?: number;
    notify?: boolean;
}

export interface MoveAgendaCenterRequestDto {
    centerId: number;
    agendaId: number;
    serviceId: number;
    appointmentDate: string;
}

export interface DayBusinessHours {
    day: number;
    openTime: string;
    closeTime: string;
    dayOfWeek: number;
    afternoonOpenTime?: string | null;
    afternoonCloseTime?: string | null;
}

export interface CenterBusinessHours {
    centerId: number;
    centerName: string;
    businessHours: DayBusinessHours[];
}

export interface AgendaListResponse {
    agendas: Agenda[];
    businessHours: CenterBusinessHours[];
}

export interface ServiceOrderProduct {
    id: number;
    product: AgendaProduct;
    quantity: number;
}

export interface ServiceOrderItem {
    id: number;
    product: AgendaProduct;
    quantity: number;
}

export interface AssociatedServiceOrder {
    externalId: string;
    status: number;
    items: ServiceOrderItem[];
}

export interface AgendaProduct {
    id: number;
    code: string;
    name: string;
    category?: string | null;
    subcategory?: string | null;
    pvp: number;
    brand: string;
}

export enum SmartAssignErrorType {
    NoSpecialization = 1,
    NoTimeAvailable = 2,
    OnHolidayOrVacation = 3,
    NoBoxAvailable = 4,
    IncompleteData = 5,
}

export enum SmartAssignReason {
    OnlySpecialist = 1,
    MostAvailableTime = 2,
    RotationBalance = 3,
}

export interface SmartAssignRequest {
    centerId: number;
    serviceTypeId: number;
    agendaDateTimeUtc: string;
    duration?: string;
    vehicleType?: number;
    boxId?: number;
    stationId?: number;
}

export interface SmartAssignResponse {
    success: boolean;
    assignedEmployeeId?: number;
    assignedEmployeeName?: string;
    assignedBoxId?: number;
    assignedBoxName?: string;
    reason?: SmartAssignReason;
    tooltipMessage?: string;
    errorType?: SmartAssignErrorType;
    errorMessage?: string;
    eligibleTechniciansCount: number;
}

export interface SmartAssignBulkRequest {
    centerId: number;
    agendaIds: number[];
}

export interface SmartAssignBulkItemResult {
    agendaId: number;
    protocolOrderId?: string;
    success: boolean;
    assignedEmployeeId?: number;
    assignedEmployeeName?: string;
    assignedBoxId?: number;
    errorType?: number;
    errorMessage?: string;
    serviceName: string;
}

export interface SmartAssignProtocolBulkRequest {
    centerId: number;
    agendaIds: number[];
}

export interface SmartAssignBulkResponse {
    totalRequested: number;
    totalAssigned: number;
    totalFailed: number;
    results: SmartAssignBulkItemResult[];
}

export function getSmartAssignErrorTranslationKey(errorType?: SmartAssignErrorType): string {
    switch (errorType) {
        case SmartAssignErrorType.NoSpecialization:
            return 'calendar.smart_assign.error_no_specialization';
        case SmartAssignErrorType.NoTimeAvailable:
            return 'calendar.smart_assign.error_no_time_available';
        case SmartAssignErrorType.OnHolidayOrVacation:
            return 'calendar.smart_assign.error_on_holiday';
        case SmartAssignErrorType.NoBoxAvailable:
            return 'calendar.smart_assign.error_no_box_available';
        case SmartAssignErrorType.IncompleteData:
            return 'calendar.smart_assign.error_incomplete_data';
        default:
            return 'calendar.smart_assign.error';
    }
}
